import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../api/axios';
import toast from 'react-hot-toast';
import {
  MdAdd, MdDelete, MdClose, MdToggleOn, MdToggleOff, MdFolder, MdArrowBack,
} from 'react-icons/md';

// Server sends scheduled_date as a literal "YYYY-MM-DDTHH:mm:ss" wall-clock string
// (IST) — slice instead of parsing as a Date to avoid any browser-timezone shift.
const dateKeyOf = (scheduledDate) => (scheduledDate ? scheduledDate.slice(0, 10) : 'evergreen');
const todayKey = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
};
const formatDateLabel = (dateKey) => {
  // Dates are required going forward — this only shows up for old rows saved before that.
  if (dateKey === 'evergreen') return '⚠️ No date set (needs fixing)';
  const label = new Date(`${dateKey}T00:00:00`).toLocaleDateString(undefined, {
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
  });
  return dateKey === todayKey() ? `Today · ${label}` : label;
};

/* ------------------------------------------------------------------ */
/* Phone-ratio preview of the quote card design                        */
/* ------------------------------------------------------------------ */
function QuotePreview({ imageUrl, width = 150 }) {
  const height = Math.round((width * 20) / 9); // 2160 x 4800 == 9:20
  return (
    <div
      style={{
        position: 'relative', width, height, borderRadius: 12, overflow: 'hidden',
        background: '#111', flexShrink: 0,
      }}
    >
      {imageUrl
        ? <img src={imageUrl} alt="design" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888', fontSize: 11 }}>No design</div>}
    </div>
  );
}

/* ------------------------------------------------------------------ */
const emptyForm = { title: '', image_url: '', thumbnail_url: '', scheduled_date: '', sort_order: '', is_active: true };

export default function QuotesManager() {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [filterStatus, setFilterStatus] = useState('');
  const [selectedFolder, setSelectedFolder] = useState(null); // dateKey or null = folder list view

  const fetchQuotes = useCallback(async () => {
    try {
      const params = { limit: 200 };
      if (filterStatus) params.status = filterStatus;
      const res = await api.get('/admin/quotes', { params });
      setQuotes(res.data.data);
    } catch { toast.error('Failed to load quotes'); }
    finally { setLoading(false); }
  }, [filterStatus]);

  useEffect(() => { fetchQuotes(); }, [fetchQuotes]);

  // Date-wise "folder" grouping: newest date first, Evergreen (no date) last.
  const groupedQuotes = useMemo(() => {
    const map = new Map();
    for (const q of quotes) {
      const key = dateKeyOf(q.scheduled_date);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(q);
    }
    return Array.from(map.entries()).sort(([a], [b]) => {
      if (a === 'evergreen') return 1;
      if (b === 'evergreen') return -1;
      return b.localeCompare(a);
    });
  }, [quotes]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const uploadDesign = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      setUploading(true);
      try {
        const fd = new FormData(); fd.append('file', file);
        const cfg = { headers: { 'Content-Type': 'multipart/form-data' } };
        const main = await api.post('/admin/upload/quote-image', fd, cfg);
        let thumbUrl = '';
        try {
          const fd2 = new FormData(); fd2.append('file', file);
          const thumb = await api.post('/admin/upload/quote-thumbnail', fd2, cfg);
          thumbUrl = thumb.data.data.url;
        } catch { /* thumbnail optional */ }
        setForm(p => ({ ...p, image_url: main.data.data.url, thumbnail_url: thumbUrl }));
        toast.success('Design uploaded');
      } catch { toast.error('Upload failed'); }
      finally { setUploading(false); }
    };
    input.click();
  };

  const create = async (e) => {
    e.preventDefault();
    if (!form.image_url) return toast.error('Upload a design');
    if (!form.scheduled_date) return toast.error('Pick a date');
    try {
      await api.post('/admin/quotes', {
        title: form.title || null,
        image_url: form.image_url,
        thumbnail_url: form.thumbnail_url || null,
        scheduled_date: form.scheduled_date || null,
        sort_order: Number(form.sort_order) || 0,
        is_active: form.is_active,
      });
      toast.success('Quote added');
      setForm(emptyForm);
      setShowForm(false);
      fetchQuotes();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const toggle = async (q) => {
    try { await api.put(`/admin/quotes/${q.id}`, { is_active: !q.is_active }); fetchQuotes(); }
    catch { toast.error('Toggle failed'); }
  };

  const [deleteModal, setDeleteModal] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const confirmDelete = async () => {
    if (!deleteModal) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/quotes/${deleteModal.id}`);
      toast.success('Quote deleted successfully.');
      setDeleteModal(null);
      fetchQuotes();
    } catch {
      toast.error('Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <h2>Quotes</h2>
      </div>

      <div className="card">
        <div className="section-header">
          <h3>Quote Cards</h3>
          <div className="flex-row items-center gap-2">
            <select className="filter-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ minWidth: 140 }}>
              <option value="">All Statuses</option>
              <option value="Draft">Draft</option>
              <option value="Scheduled">Scheduled</option>
              <option value="Live">Live</option>
              <option value="Expired">Expired</option>
            </select>
            <button
              className="btn btn-sm btn-primary"
              onClick={() => {
                if (!showForm && selectedFolder && selectedFolder !== 'evergreen') {
                  setForm(p => ({ ...p, scheduled_date: selectedFolder }));
                }
                setShowForm(!showForm);
              }}
            >
              {showForm ? <><MdClose /> Cancel</> : <><MdAdd /> Add Quote</>}
            </button>
          </div>
        </div>

        {showForm && (
          <form className="card form-card" onSubmit={create} style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
            {/* Left: live preview */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
              <QuotePreview imageUrl={form.image_url} width={170} />
              <button type="button" className="btn btn-sm btn-ghost" onClick={uploadDesign} disabled={uploading}>
                {uploading ? 'Uploading…' : (form.image_url ? 'Replace design' : 'Upload design')}
              </button>
              <span className="text-muted" style={{ fontSize: 11, textAlign: 'center', maxWidth: 180 }}>
                2160 × 4800 px quote card design.
              </span>
            </div>

            {/* Right: fields */}
            <div style={{ flex: 1, minWidth: 240 }}>
              <div className="form-group">
                <label>Title (optional, internal)</label>
                <input value={form.title} onChange={e => set('title', e.target.value)} placeholder="e.g. Monday Motivation" />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Show on date * (visible all day)</label>
                  <input type="date" value={form.scheduled_date} onChange={e => set('scheduled_date', e.target.value)} required />
                </div>
                <div className="form-group">
                  <label>Sort order</label>
                  <input type="number" value={form.sort_order} onChange={e => set('sort_order', e.target.value)} placeholder="0" />
                </div>
              </div>
              <label className="checkbox-label">
                <input type="checkbox" checked={form.is_active} onChange={e => set('is_active', e.target.checked)} />
                Active (visible in app)
              </label>
              <button type="submit" className="btn btn-primary mt-md" disabled={uploading}>Add Quote</button>
            </div>
          </form>
        )}

        {loading ? <p className="text-muted">Loading…</p> : selectedFolder === null ? (
          /* ---------------- Folder list view ---------------- */
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 16, marginTop: 12 }}>
            {groupedQuotes.map(([dateKey, items]) => (
              <div
                key={dateKey}
                className="card"
                onClick={() => setSelectedFolder(dateKey)}
                style={{ padding: 14, cursor: 'pointer', textAlign: 'center' }}
                title={`Open ${formatDateLabel(dateKey)}`}
              >
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <MdFolder size={56} style={{ color: '#f59e0b' }} />
                  <span style={{
                    position: 'absolute', top: -6, right: -10, background: '#dc2626', color: '#fff',
                    borderRadius: 10, fontSize: 11, fontWeight: 700, padding: '1px 6px', lineHeight: '16px',
                  }}>{items.length}</span>
                </div>
                <div style={{ fontSize: 13, fontWeight: 600, marginTop: 8 }}>{formatDateLabel(dateKey)}</div>
                <div className="text-muted" style={{ fontSize: 11, marginTop: 2 }}>{items.length} quote{items.length === 1 ? '' : 's'}</div>
              </div>
            ))}
            {groupedQuotes.length === 0 && <p className="text-muted">No quotes yet</p>}
          </div>
        ) : (
          /* ---------------- Folder contents view ---------------- */
          <div style={{ marginTop: 12 }}>
            <button className="btn btn-sm btn-ghost" onClick={() => setSelectedFolder(null)} style={{ marginBottom: 10 }}>
              <MdArrowBack /> All folders
            </button>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10,
              paddingBottom: 6, borderBottom: '1px solid #e5e7eb',
            }}>
              <MdFolder style={{ color: '#f59e0b' }} size={18} />
              <strong>{formatDateLabel(selectedFolder)}</strong>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 16 }}>
              {(groupedQuotes.find(([k]) => k === selectedFolder)?.[1] || []).map(q => {
                let statusBadge = null;
                if (q.status === 'Scheduled') statusBadge = <span className="badge" style={{background:'#FEF08A', color:'#854D0E'}}>🟡 Scheduled</span>;
                else if (q.status === 'Live') statusBadge = <span className="badge" style={{background:'#BBF7D0', color:'#166534'}}>🟢 Live</span>;
                else if (q.status === 'Draft') statusBadge = <span className="badge" style={{background:'#E5E7EB', color:'#374151'}}>⚪ Draft</span>;
                else if (q.status === 'Expired') statusBadge = <span className="badge badge-expired">🔴 Expired</span>;

                return (
                <div key={q.id} className="card" style={{ padding: 10 }}>
                  <QuotePreview imageUrl={q.thumbnail_url || q.image_url} width={130} />
                  <div style={{ marginTop: 8 }}>
                    {statusBadge}
                    {q.title && <div style={{ fontSize: 12, marginTop: 2 }}>{q.title}</div>}
                    <div className="text-muted" style={{ fontSize: 11, marginTop: 2 }}>{q.share_count || 0} shares</div>
                  </div>
                  <div className="action-btns" style={{ marginTop: 6 }}>
                    <button className="btn btn-sm btn-ghost" onClick={() => toggle(q)} title={q.is_active ? 'Hide' : 'Show'}>
                      {q.is_active ? <MdToggleOn className="text-green" /> : <MdToggleOff />}
                    </button>
                    <button className="btn btn-sm btn-danger" onClick={() => setDeleteModal(q)} title="Delete Quote"><MdDelete /></button>
                  </div>
                </div>
              )})}
              {!groupedQuotes.some(([k]) => k === selectedFolder) && <p className="text-muted">This folder is empty</p>}
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteModal && (
          <div className="modal-overlay" onClick={() => setDeleteModal(null)}>
            <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Delete Quote</h3>
                <button className="modal-close" onClick={() => setDeleteModal(null)}><MdClose /></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete this quote?</p>
                <p className="text-muted" style={{ marginTop: 8 }}>"{deleteModal.title || 'Untitled Quote'}"</p>
                <p className="text-muted" style={{ marginTop: 8 }}>This action cannot be undone.</p>
              </div>
              <div className="modal-footer">
                <button className="btn btn-ghost" onClick={() => setDeleteModal(null)}>Cancel</button>
                <button className="btn btn-danger-solid" onClick={confirmDelete} disabled={deleting}>
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
